import React, { useState } from 'react';

import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import COLOURS from '../../constants/colour';
import { useAuth } from '../../hooks/useAuth';
import { updateOfferStatus, uploadOfferImageNoCallback } from '../../services/c2dcrm';
import { MerchantOfferEdit } from '../../types/offer';
import MerchantOfferEditForm from '../Merchant/MerchantOfferEditForm';
import { useModalContext } from '../Context/ModalContext';
import { formatDate } from '../../utils/dates';
import { Button, CardMedia, Chip, FormControl, InputLabel, MenuItem, Select, useTheme } from '@material-ui/core';
import FileProcessInfo from '../FileProcessInfo';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardActions from '@material-ui/core/CardActions';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import { DataPass } from '../../types/dataPassport';
import { useFilePicker } from 'use-file-picker';

type PropsType = {
  offer: MerchantOfferEdit;
  passes: DataPass[];
};

const generateKey = (pre: string): string => {
  return `${pre}_${new Date().getTime()}_${Math.random().toString(20).substr(2, 20)}`
}

const getStatusOption = (current: string): string[] => {
  switch (current) {
    case 'new':
      return (['new', 'pending_approval']);

    case 'pending_approval':
      return (['new', 'pending_approval']);

    case 'approved':
      return (['active', 'approved', 'new']);

    case 'inactive':
      return (['inactive', 'new']);

    case 'active':
      return (['active', 'inactive', 'new']);

    case 'rejected':
      return (['new', 'rejected']);

    default:
      return ([]);

  }
}

const getFriendlyStatus = (current: string): string => {
  if (current === 'pending_approval') {
    return ('Submitted for Approval');
  }
  else if (current === 'new') {
    return ('Editing');
  }
  else {
    return current.charAt(0).toUpperCase() + current.slice(1);
  }
};

const MerchantOfferCard: React.FC<PropsType> = ({
  offer,
  passes
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { token } = useAuth();
  const { openModal, closeModal } = useModalContext();
  const [off, setOff] = useState({ o: offer });
  const [openFileSelector, { clear, plainFiles }] = useFilePicker({
    multiple: false,
    readFilesContent: false,
    accept: '.png, .jpeg, .jpg',
  });

  function fakeUpdateState(offer: MerchantOfferEdit) {
    setOff({ o: offer });
  }

  const openEditModal = (offer: MerchantOfferEdit) => {
    openModal({
      open: true,
      component: MerchantOfferEditForm,
      componentProps: {
        offer,
        token,
        closeModal,
        fakeUpdateState,
      },
    });
  };

  const handleChange = (event: any): void => {
    if (offer.status === "approved" && event.target.value === "active") {
      offer.status = event.target.value;
      setOff({ o: offer });
      updateStatus(event.target.value);
    }
    else if (offer.status !== "approved" && event.target.value === "active") {
      return;
    }
    else {
      offer.status = event.target.value;
      setOff({ o: offer });
      updateStatus(event.target.value);
    }
  };

  const updateStatus = (status: string): string => {
    updateOfferStatus(token, offer, status);
    return status;
  }

  const upload = async (offer: MerchantOfferEdit) => {
    if (plainFiles && plainFiles?.length > 0) {
      const currentFile = plainFiles[0];
      let imageExt = "png";
      const maybeImageExt = plainFiles[0].name.split('.');
      if (maybeImageExt.length > 2) { 
        imageExt = maybeImageExt[maybeImageExt.length - 1];  
      }
    
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      uploadOfferImageNoCallback(currentFile, token, offer.id).then((response) => {
        // https://media.datapassport.<env>/sejutakg/<merchant_offer_id>-offer_image.<image_ext>
        offer.offer_image_url = `https://media.datapassport.dev/sejutakg/${offer.id}-offer_image.${imageExt}`;
        console.log(offer.offer_image_url);
        setOff({ o: offer });
        clear();
        showImageInfo();
      })
    }
  };


  const showImageInfo = () => {
    openModal({
      open: true,
      component: FileProcessInfo,
    });
  }

  const makeAvatar = (status: string) => {
    if (status === 'active') {
      return (<Chip style={{ backgroundColor: 'green', color: 'white' }} avatar={<Avatar style={{ backgroundColor: 'lightgreen', color: 'black' }} aria-label={status}>A</Avatar>} label="Active" />);
    }
    else if (status === 'pending_approval') {
      return (<Chip style={{ backgroundColor: 'orange', color: 'white' }} avatar={<Avatar style={{ backgroundColor: 'white', color: 'black' }} aria-label={status}>PA</Avatar>} label="Pending Approval" />);
    }
    else {
      return (<Chip style={{ backgroundColor: 'gray', color: 'white' }} avatar={<Avatar style={{ backgroundColor: 'lightgrey', color: 'black' }} aria-label={status}>IN</Avatar>} label="Inactive" />);
    }
  }

  return (
    <div className={classes.container} key={generateKey('card')}>
      <Grid container spacing={4}>
        <Grid item>
          <Card className={classes.card}>
            <CardHeader
              avatar={makeAvatar(off.o.status)}
              action={
                <IconButton aria-label="Edit Offer" onClick={() => openEditModal(off.o)}>
                  <EditIcon />
                </IconButton>
              }
              title=""
            />
            <CardContent>
              <Typography className={classes.leftText} variant="h2" component="div">
                {off.o.title}
              </Typography>
            </CardContent>
            <CardMedia
              component="img"
              image={(off.o.offer_image_url === ('' || null)) ? 'https://images.squarespace-cdn.com/content/v1/611386630a0d433d1854d656/1633288429020-RAJHS2AFJD941OZB1AYV/1FAE1105-E04D-4B51-B0BE-4B826DF326A2' : off.o.offer_image_url}
              alt={off.o.title}
            />
            <CardContent>
              <Typography className={classes.leftText} variant="h6" component="div">
                Offer Details
              </Typography>
              <Typography className={classes.leftText} style={{ paddingBottom: '10px' }} gutterBottom component="div">
                {off.o.details}
              </Typography>
              <Typography className={classes.leftText} variant="h6" component="div">
                Tags
              </Typography>
              <Typography className={classes.leftText} variant="h6" component="div">
                {off.o.tags.map((tag) => <Chip key={generateKey('chip')} color="primary" variant="outlined" className={classes.rightPadding} size="small" label={tag} component="a" href={"/tags/#" + tag} clickable />)}
              </Typography>
              <Typography className={classes.leftText} variant="h6" component="div">
                Offer Start and End Dates
              </Typography>
              <Typography className={classes.leftText} style={{ paddingBottom: '10px' }} gutterBottom component="div">
                Offer valid from: {formatDate(off.o.start_date)} {(off.o.end_date ? `to ${formatDate(off.o.end_date)}` : '')}
              </Typography>
              <Typography className={classes.leftText} variant="h6" component="div">
                Offer URL:
              </Typography>
              <Typography className={classes.leftText} style={{ paddingBottom: '10px' }} gutterBottom component="div">
                Offer URL: <a href={off.o.offer_url} target="_blank" rel="noopener noreferrer">{off.o.offer_url}</a>
              </Typography>
              <Typography className={classes.leftText} gutterBottom variant="body2" component="div">
                This offer is currently active on these Data Passes:
              </Typography>
              <Typography className={classes.leftText} gutterBottom variant="body2" component="div">
                <ul>
                  {offer.data_passes.map((data_pass) => (<li key={generateKey('offerpass')}>({passes.find(p => p.id === data_pass)?.name}) - {passes.find(p => p.id === data_pass)?.title}</li>))}
                </ul>
              </Typography>
            </CardContent>
            <CardActions>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <div>
                    <button onClick={() => openFileSelector()}>Choose an Offer Image</button>
                    <br />
                    {(plainFiles.length > 0) &&
                      <Button
                        className="btn-upload"
                        color="primary"
                        variant="contained"
                        component="span"
                        onClick={() => {
                          upload(off.o)
                        }}
                      >
                      Upload
                    </Button>}
                  </div>
                </Grid>
                {(off.o.status !== 'rejected') ? <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="current-status-label">Current Status</InputLabel>
                    <Select
                      labelId="current-status-label"
                      id="status-select"
                      value={off.o.status}
                      label="Current Status"
                      onChange={handleChange}
                    >
                      {getStatusOption(off.o.status).map(x => <MenuItem key={generateKey(x)} value={x} selected={off.o.status === x}>{getFriendlyStatus(x)}</MenuItem>,)}
                    </Select>
                  </FormControl>
                </Grid> :
                  <Grid item xs={6}>
                    Your offer was Rejected, please edit to restart the Approval Process.
                  </Grid>
                }
              </Grid>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </div >
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'grid',
    gridGap: '2px',
    placeItems: 'center',
    textAlign: 'center',
    marginBottom: '0px',
    boxShadow: '0px 5px 26px rgba(0, 0, 0, 0.2)',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.up('xs')]: {
      minWidth: '248px',
      backgroundColor: 'red',
    },
    [theme.breakpoints.up('sm')]: {
      width: '486px',
      backgroundColor: 'green',
    },
    [theme.breakpoints.up('md')]: {
      width: '686px',
      backgroundColor: 'blue',
    },
    [theme.breakpoints.up('lg')]: {
      width: '886px',
      backgroundColor: 'purple',
    },
  },
  card: {
    marginBottom: '0px',
    boxShadow: '0px 5px 26px rgba(0, 0, 0, 0.2)',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.up('xs')]: {
      width: '320px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '486px',
    },
    [theme.breakpoints.up('md')]: {
      width: '686px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '886px',
    },
  },

  codeText: {
    marginTop: '8px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: COLOURS.MediumGrey,

    '& img': {
      height: '18px',
      marginRight: '8px',
    },
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  leftText: {
    alignItems: 'left',
    textAlign: 'left',
  },
  leftTextMuted: {
    alignItems: 'left',
    textAlign: 'left',
    color: 'grey',
    fontWeight: 'normal',
  },
  wide: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  rightPadding: {
    marginRight: '2px',
  },
}));

export default MerchantOfferCard;
